'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

/**
 * Starter chips of the Ask panel (plan 03 CHG-05, CHG-13). The questions come
 * from Setting `briefing_suggested_prompts` (the same chips as the Command
 * Briefing AskBar, so the two surfaces suggest the same things), plus
 * "Explain this page", which the panel answers for whichever page is open.
 * Before the Setting arrives only "Explain this page" shows.
 */
const EXPLAIN_PAGE = 'Explain this page'

interface CapabilityBubblesProps {
  prompts: string[]
  onSelect: (query: string) => void
}

export function CapabilityBubbles({ prompts, onSelect }: CapabilityBubblesProps) {
  const chips = [...prompts.filter((p) => p.trim() && p !== EXPLAIN_PAGE), EXPLAIN_PAGE]
  return (
    <ul className='flex flex-wrap justify-center gap-2 p-2' aria-label='Suggested questions'>
      {chips.map((chip, i) => {
        const Icon = chip === EXPLAIN_PAGE ? Icons.info : Icons.sparkles
        return (
          <motion.li
            key={chip}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25, ease: 'easeOut' }}
          >
            <button
              type='button'
              onClick={() => onSelect(chip)}
              className={cn(
                'flex items-center gap-2 rounded-full px-3 py-2',
                'border border-hairline bg-white',
                'text-sm font-medium text-brand-navy shadow-sm',
                'transition-colors duration-200',
                'hover:border-brand-cornflower hover:bg-tab-track',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
              )}
            >
              <Icon aria-hidden='true' className='h-4 w-4 text-brand-purple' strokeWidth={1.5} />
              <span>{chip}</span>
            </button>
          </motion.li>
        )
      })}
    </ul>
  )
}
